import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Eye, EyeOff, ArrowRight, ArrowLeft, Phone, Bot, CheckCircle2, Wrench, Droplets, Flame, HardHat, Bath, Headset, Sun, Home, Shield, Car, Briefcase, Building2, MoreHorizontal } from 'lucide-react';
import { toast } from 'sonner';
import { ThemeToggle } from '../components/ThemeToggle';

const PRODUCTS = [
  {
    id: 'agent9',
    name: 'Agent9',
    tagline: 'AI phone assistant for your business',
    icon: Bot,
    color: 'emerald',
    features: ['AI phone answering', 'Lead capture & CRM', 'Appointment booking', 'Call analytics'],
  },
  {
    id: 'voicedesk',
    name: 'VoiceDesk',
    tagline: 'AI-powered business phone routing',
    icon: Phone,
    color: 'blue',
    features: ['Smart IVR routing', 'AI receptionist', 'Department management', 'Call analytics'],
  },
  {
    id: 'tele_sale',
    name: 'Tele Sale',
    tagline: 'Company phone system for sales teams',
    icon: Headset,
    color: 'violet',
    exclusive: true,
    features: ['A US number per salesperson', 'Calls & SMS', 'Leads & pipeline', 'Supervisor & AI tools'],
  },
];

// Mirrors backend telesales.config.BUSINESS_TYPES
const BUSINESS_TYPES = [
  { id: 'solar', name: 'Solar', icon: Sun, description: 'Residential & commercial solar sales' },
  { id: 'roofing', name: 'Roofing', icon: Home, description: 'Roof replacement & repair sales' },
  { id: 'home_services', name: 'Home Services', icon: Wrench, description: 'HVAC, windows, remodeling and more' },
  { id: 'insurance', name: 'Insurance', icon: Shield, description: 'Life, health, auto, home policies' },
  { id: 'automotive', name: 'Automotive', icon: Car, description: 'Dealerships, service & warranty' },
  { id: 'b2b_sales', name: 'B2B Sales', icon: Briefcase, description: 'Outbound & inbound business sales' },
  { id: 'real_estate_leads', name: 'Real Estate Leads', icon: Building2, description: 'Buyer & seller lead teams' },
  { id: 'other', name: 'Other', icon: MoreHorizontal, description: 'Any other sales team' },
];

const PRODUCT_STYLE = {
  emerald: { border: 'border-emerald-500 ring-1 ring-emerald-500/30', icon: 'bg-emerald-500/10 text-emerald-500', check: 'text-emerald-500', chip: 'bg-emerald-500/15 text-emerald-500' },
  blue: { border: 'border-blue-500 ring-1 ring-blue-500/30', icon: 'bg-blue-500/10 text-blue-500', check: 'text-blue-500', chip: 'bg-blue-500/15 text-blue-500' },
  violet: { border: 'border-violet-500 ring-1 ring-violet-500/30', icon: 'bg-violet-500/10 text-violet-500', check: 'text-violet-500', chip: 'bg-violet-500/15 text-violet-500' },
};

const INDUSTRIES = [
  { id: 'real_estate', name: 'Real Estate', icon: Bot, description: 'Agents, brokers, property management' },
  { id: 'general_construction', name: 'General Construction', icon: HardHat, description: 'Remodeling, additions, new builds' },
  { id: 'drywall', name: 'Drywall', icon: Wrench, description: 'Installation, repair, texturing' },
  { id: 'plumbing', name: 'Plumbing', icon: Droplets, description: 'Repairs, installation, drain cleaning' },
  { id: 'hvac', name: 'Heating & Air / HVAC', icon: Flame, description: 'Heating, cooling, maintenance' },
  { id: 'bathroom_remodeling', name: 'Bathroom Remodeling', icon: Bath, description: 'Showers, tubs, tile, remodel estimates' },
];

export default function SignupPage() {
  const navigate = useNavigate();
  const { signup, loginWithGoogle } = useAuth();
  const [step, setStep] = useState(1); // 1=product, 2=industry, 3=form
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedIndustry, setSelectedIndustry] = useState('');
  const [selectedBusinessType, setSelectedBusinessType] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({
    full_name: '', email: '', password: '',
    company_name: '', phone: '',
  });

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  // Tele Sale is a separate company account, so it can't be combined with the other products.
  const toggleProduct = (id) => {
    const exclusive = PRODUCTS.find(p => p.id === id)?.exclusive;
    setSelectedProducts(prev => {
      if (prev.includes(id)) return prev.filter(p => p !== id);
      if (exclusive) return [id];
      return [...prev.filter(p => !PRODUCTS.find(x => x.id === p)?.exclusive), id];
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedProducts.length === 0) {
      toast.error('Please select at least one product');
      setStep(1);
      return;
    }
    if (!form.phone.trim()) {
      setError('Phone number is required for call routing and account verification');
      return;
    }
    setLoading(true);
    setError('');

    const result = await signup({
      ...form,
      products: selectedProducts,
      industry: isTeleSale ? undefined : (selectedIndustry || undefined),
      business_type: isTeleSale ? selectedBusinessType : undefined,
    });

    if (result.success) {
      if (result.pending_verification) {
        navigate('/signup/verify');
      } else {
        toast.success('Account created!');
        const products = result.data?.user?.products || selectedProducts;
        if (products.includes('tele_sale')) navigate('/telesales/setup');
        else if (products.length > 1) navigate('/select-product');
        else if (products[0] === 'voicedesk') navigate('/voicedesk/onboarding');
        else navigate('/onboarding');
      }
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  // Industry step for Agent9; business-type step for Tele Sale
  const isTeleSale = selectedProducts.includes('tele_sale');
  const showIndustryStep = selectedProducts.includes('agent9') || isTeleSale;
  const stepTwoOptions = isTeleSale ? BUSINESS_TYPES : INDUSTRIES;
  const stepTwoValue = isTeleSale ? selectedBusinessType : selectedIndustry;
  const setStepTwoValue = isTeleSale ? setSelectedBusinessType : setSelectedIndustry;

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-12" data-testid="signup-page">
      <div className="fixed top-4 right-4 z-50"><ThemeToggle compact /></div>
      <div className="w-full max-w-lg">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-4">
            <div className="w-9 h-9 rounded-lg overflow-hidden shrink-0">
              <img src="/assets/agent9-icon.png" alt="Agent9" className="w-full h-full object-contain" />
            </div>
            <span className="text-xl font-bold text-foreground tracking-tight">Agent9</span>
          </Link>
          <h1 className="text-2xl font-bold text-foreground mt-4" data-testid="signup-heading">
            {step === 1 ? 'Choose your product' : step === 2 ? (isTeleSale ? 'What does your team sell?' : 'Select your industry') : 'Create your account'}
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            {step === 1 ? 'Select the product that fits your business' : step === 2 ? (isTeleSale ? 'We\'ll set up your pipeline and AI tools for your business' : 'We\'ll tailor your AI assistant to your industry') : (isTeleSale ? 'You\'ll be the owner of your company account' : 'One account for all products')}
          </p>
        </div>

        {step === 1 && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4">
              {PRODUCTS.map((p) => {
                const selected = selectedProducts.includes(p.id);
                const Icon = p.icon;
                const style = PRODUCT_STYLE[p.color];
                const borderColor = selected ? style.border : 'border-border hover:border-muted-foreground/30';
                const iconBg = style.icon;

                return (
                  <button key={p.id} onClick={() => toggleProduct(p.id)}
                    className={`relative w-full text-left p-5 rounded-xl border-2 bg-card backdrop-blur-sm transition-all ${borderColor}`}
                    data-testid={`product-card-${p.id}`}>
                    {selected && (
                      <div className="absolute top-3 right-3">
                        <CheckCircle2 size={20} className={style.check} />
                      </div>
                    )}
                    <div className="flex items-start gap-4">
                      <div className={`w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0 ${iconBg}`}>
                        <Icon size={22} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-base font-semibold text-foreground">{p.name}</h3>
                        <p className="text-sm text-muted-foreground mt-0.5">{p.tagline}</p>
                        <div className="flex flex-wrap gap-2 mt-3">
                          {p.features.map((f, i) => (
                            <span key={i} className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">{f}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            <Button onClick={() => {
              if (selectedProducts.length === 0) { toast.error('Please select at least one product'); return; }
              if (showIndustryStep) { setStep(2); } else { setStep(3); }
            }} className="w-full h-11 bg-primary hover:bg-primary/90 text-primary-foreground font-medium mt-4" data-testid="continue-to-form-btn">
              Continue <ArrowRight size={16} className="ml-1" />
            </Button>

            <p className="text-center text-muted-foreground text-sm mt-4">
              Already have an account? <Link to="/login" className="text-primary hover:text-primary/80 font-medium" data-testid="login-link">Log in</Link>
            </p>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <button onClick={() => setStep(1)} className="text-muted-foreground hover:text-foreground transition-colors" data-testid="back-to-products-btn">
                <ArrowLeft size={18} />
              </button>
              <span className="text-sm text-muted-foreground">Step 2 of 3</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {stepTwoOptions.map((ind) => {
                const selected = stepTwoValue === ind.id;
                const Icon = ind.icon;
                return (
                  <button key={ind.id} onClick={() => setStepTwoValue(ind.id)}
                    className={`relative w-full text-left p-4 rounded-xl border-2 bg-card transition-all ${selected ? 'border-emerald-500 ring-1 ring-emerald-500/30' : 'border-border hover:border-muted-foreground/30'}`}
                    data-testid={`${isTeleSale ? 'business-type' : 'industry'}-card-${ind.id}`}>
                    {selected && (
                      <div className="absolute top-2.5 right-2.5">
                        <CheckCircle2 size={18} className="text-emerald-500" />
                      </div>
                    )}
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 bg-emerald-500/10 text-emerald-500">
                        <Icon size={18} />
                      </div>
                      <div className="flex-1 min-w-0 pr-6">
                        <h3 className="text-sm font-semibold text-foreground">{ind.name}</h3>
                        <p className="text-xs text-muted-foreground mt-0.5">{ind.description}</p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            <Button onClick={() => {
              if (!stepTwoValue) { toast.error(isTeleSale ? 'Please select your business type' : 'Please select your industry'); return; }
              setStep(3);
            }} className="w-full h-11 bg-primary hover:bg-primary/90 text-primary-foreground font-medium mt-4" data-testid="continue-to-details-btn">
              Continue <ArrowRight size={16} className="ml-1" />
            </Button>

            <p className="text-center text-muted-foreground text-sm mt-4">
              Already have an account? <Link to="/login" className="text-primary hover:text-primary/80 font-medium">Log in</Link>
            </p>
          </div>
        )}

        {step === 3 && (
          <Card className="bg-card border-border backdrop-blur-sm">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-5">
                <button onClick={() => setStep(showIndustryStep ? 2 : 1)} className="text-muted-foreground hover:text-foreground transition-colors" data-testid="back-to-industry-btn">
                  <ArrowLeft size={18} />
                </button>
                <div className="flex gap-2">
                  {selectedProducts.map(pid => {
                    const prod = PRODUCTS.find(p => p.id === pid);
                    return (
                      <span key={pid} className={`text-xs px-2.5 py-1 rounded-full font-medium ${PRODUCT_STYLE[prod?.color || 'blue'].chip}`}>
                        {prod?.name}
                      </span>
                    );
                  })}
                  {stepTwoValue && (
                    <span className="text-xs px-2.5 py-1 rounded-full font-medium bg-muted text-muted-foreground">
                      {stepTwoOptions.find(i => i.id === stepTwoValue)?.name}
                    </span>
                  )}
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-lg px-3 py-2" data-testid="signup-error">
                    {error}
                  </div>
                )}

                <div className="space-y-1.5">
                  <Label className="text-foreground text-sm">Full name</Label>
                  <Input value={form.full_name} onChange={set('full_name')} placeholder="Jane Smith" required
                    className="bg-muted border-border text-foreground placeholder:text-muted-foreground h-11" data-testid="signup-name-input" />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-foreground text-sm">Email</Label>
                  <Input type="email" value={form.email} onChange={set('email')} placeholder="you@company.com" required
                    className="bg-muted border-border text-foreground placeholder:text-muted-foreground h-11" data-testid="signup-email-input" />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-foreground text-sm">Password</Label>
                  <div className="relative">
                    <Input type={showPassword ? 'text' : 'password'} value={form.password} onChange={set('password')}
                      placeholder="Min. 8 characters" required minLength={8}
                      className="bg-muted border-border text-foreground placeholder:text-muted-foreground h-11 pr-10" data-testid="signup-password-input" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-foreground text-sm">Company name</Label>
                  <Input value={form.company_name} onChange={set('company_name')} placeholder="Your company" required
                    className="bg-muted border-border text-foreground placeholder:text-muted-foreground h-11" data-testid="signup-company-input" />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-foreground text-sm">Phone number <span className="text-destructive">*</span></Label>
                  <Input type="tel" value={form.phone} onChange={set('phone')} placeholder="+1 (555) 123-4567" required
                    className="bg-muted border-border text-foreground placeholder:text-muted-foreground h-11" data-testid="signup-phone-input" />
                  <p className="text-xs text-muted-foreground">Required for call routing, forwarding, and account verification.</p>
                </div>

                <Button type="submit" disabled={loading}
                  className="w-full h-11 bg-primary hover:bg-primary/90 text-primary-foreground font-medium" data-testid="signup-submit-btn">
                  {loading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary-foreground border-t-transparent" />
                  ) : (
                    <>Create account <ArrowRight size={16} className="ml-1" /></>
                  )}
                </Button>
              </form>

              {!isTeleSale && (<>
              <div className="relative my-5">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border" /></div>
                <div className="relative flex justify-center"><span className="bg-card px-3 text-xs text-muted-foreground">or</span></div>
              </div>

              <button onClick={loginWithGoogle}
                className="w-full h-11 flex items-center justify-center gap-2 bg-muted hover:bg-accent border border-border rounded-lg text-sm text-foreground transition-colors" data-testid="google-signup-btn">
                <svg width="16" height="16" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                Continue with Google
              </button>
              </>)}

              <p className="text-center text-muted-foreground text-sm mt-4">
                Already have an account? <Link to="/login" className="text-primary hover:text-primary/80 font-medium">Log in</Link>
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
